import React, { useEffect, useState } from "react";
import { AddButton, DeleteButton } from "../../../components/common/Button";
import { QuestionGenerator } from "./QuestionGenerator";
import { useDispatch, useSelector } from "react-redux";
import { VALIDATION_MESSAGE } from '../../../constants/messages';
import { Spin } from "antd";
import {
  setGoalsFormData,
  setGoalsAnswer,
  setIsSaved
} from "../../../redux/slices/supplierGoalsSlice";


const GenerateTabContent = ({ Data , supplierId}) => {
  const dispatch = useDispatch();
  const { goalsAnswer, goalsQuestions } = useSelector((state) => state.goals);
  const [loading, setLoading] = useState(true); // Loading state

  useEffect(() => {
    // Simulate loading for demonstration (replace this with actual API call if needed)
    const timer = setTimeout(() => {
      setLoading(false); // Set loading to false after the timeout
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const pillarAnswer = goalsAnswer?.find(
    (pillar) => pillar.pillarName === Data.pillarName
  );
  const answers = pillarAnswer?.goalAnswers;

  function checkForIdenticalFieldValues(
    data,
    questionId,
    inputId,
    value,
    answerId
  ) {
    if (!Array.isArray(data)) return false;

    const normalizedMap = {};

    data.forEach((item) => {
      if (!normalizedMap[item.questionId]) {
        normalizedMap[item.questionId] = [];
      }

      const responseObject = {};
      item.response.forEach((field) => {
        responseObject[field.fieldId] = field.value;
      });

      normalizedMap[item.questionId].push({
        questionId: item.questionId,
        answerId: item.answerId,
        response: responseObject,
      });
    });

    for (const key in normalizedMap) {
      const responses = normalizedMap[key];

      responses.forEach((item) => {
        if (item.questionId === questionId && item.answerId === answerId) {
          item.response[inputId] = value;
        }
      });
    }

    for (const key in normalizedMap) {
      const responses = normalizedMap[key];

      if (responses.length > 1) {
        for (let i = 0; i < responses.length; i++) {
          const firstResponse = responses[i].response;

          for (let j = i + 1; j < responses.length; j++) {
            const currentResponse = responses[j].response;

            const isIdentical = Object.keys(firstResponse).every(
              (fieldId) => firstResponse[fieldId] === currentResponse[fieldId]
            );

            if (isIdentical) {
              return true;
            }
          }
        }
      }
    }

    return false;
  }

  const handleInputChange = (pillarName, questionId, inputId, value, answerId) => {
    dispatch(setIsSaved(false));
    const pillarIndex = goalsAnswer?.findIndex((p) => p.pillarName === pillarName);
    const hasIdenticalValues = checkForIdenticalFieldValues(
      goalsAnswer[pillarIndex]?.goalAnswers,
      questionId,
      inputId,
      value,
      answerId
    );

    if (!hasIdenticalValues) {
      dispatch(
        setGoalsFormData({ pillarName, questionId, inputId, value, answerId,supplierId })
      );
    } else {
      alert(VALIDATION_MESSAGE.duplicateValidation);
    }
  };

  const addAnswer = (questionId, goalAnswers) => {
    const relatedAnswers = goalAnswers.filter(
      (answer) => answer.questionId === questionId
    );

    const newAnswerIdNum =
      relatedAnswers.length > 0
        ? Math.max(...relatedAnswers.map((ans) => parseInt(ans.answerId.split("_")[2]))) + 1
        : 1;

    const newAnswerId = `ans_id_${newAnswerIdNum}`;

    return {
      questionId: questionId,
      answerId: newAnswerId,
      goalStatus: "Active",
      response: [],
    };
  };

  const deleteAnswer = (questionId, answerIdToDelete, goalAnswers) => {
    const filteredAnswers = goalAnswers?.filter(
      (answer) => !(answer.questionId === questionId && answer.answerId === answerIdToDelete)
    );

    let relatedAnswers = filteredAnswers?.filter(
      (answer) => answer.questionId === questionId
    );

    relatedAnswers = relatedAnswers?.map((answer, index) => ({
      ...answer,
      answerId: `ans_id_${index + 1}`,
    }));

    return [
      ...filteredAnswers.filter((answer) => answer.questionId !== questionId),
      ...relatedAnswers,
    ];
  };

  const fieldValueCheck = (inputFields, response) => {
    const isValid = inputFields?.every((inputField) => {
      const matchedResponse = response?.find(
        (resp) => resp.fieldId === inputField.inputId
      );
      return matchedResponse && matchedResponse.value.trim() !== "";
    });

    return !isValid;
  };

  const handleAddQuestion = (pillarName, questionId) => {
    const questionIndex = goalsQuestions?.findIndex(
      (que) => que.pillarName === pillarName
    );
    const inputFields = goalsQuestions[questionIndex]?.questions?.find(
      (que) => que.questionId === questionId
    )?.inputFields;

    const answerIndex = goalsAnswer?.findIndex(
      (ans) => ans.pillarName === pillarName
    );
    const filteredAnswer = goalsAnswer[answerIndex]?.goalAnswers?.filter(
      (ans) => ans.questionId === questionId
    );

    if (!filteredAnswer || filteredAnswer.length === 0) {
      alert(VALIDATION_MESSAGE.addQuestion);
      return;
    }

    for (const ans of filteredAnswer) {
      const result = fieldValueCheck(inputFields, ans?.response);
      if (result) {
        alert(VALIDATION_MESSAGE.addQuestion);
        return;
      }
    }

    let updatedPillarAnswer = [...goalsAnswer];
    const pillarIndex = updatedPillarAnswer?.findIndex(
      (pillar) => pillar.pillarName === pillarName
    );

    if (pillarIndex !== -1) {
      const updatedGoalAnswers = [...updatedPillarAnswer[pillarIndex].goalAnswers];
      const newAnswer = addAnswer(questionId, updatedGoalAnswers);
      updatedGoalAnswers.push(newAnswer);

      updatedPillarAnswer[pillarIndex] = {
        ...updatedPillarAnswer[pillarIndex],
        goalAnswers: updatedGoalAnswers,
      };

      dispatch(setGoalsAnswer(updatedPillarAnswer));
    }
  };

  const handleDeleteQuestion = (pillarName, questionId, answerId) => {
    let updatedPillarAnswer = [...goalsAnswer];
    const pillarIndex = updatedPillarAnswer?.findIndex(
      (pillar) => pillar.pillarName === pillarName
    );

    if (pillarIndex !== -1) {
      let updatedGoalAnswers = updatedPillarAnswer[pillarIndex].goalAnswers;
      updatedGoalAnswers = deleteAnswer(
        questionId,
        answerId,
        updatedGoalAnswers
      );

      updatedPillarAnswer[pillarIndex] = {
        ...updatedPillarAnswer[pillarIndex],
        goalAnswers: updatedGoalAnswers,
      };

      dispatch(setGoalsAnswer(updatedPillarAnswer));
    }
  };

  const questionContent = (Data) => {
    return (
      <div className="h-full w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {Data.questions?.map((question) => {
          const sameResponse = answers?.filter((answer) =>
            answer.questionId.includes(question.questionId)
          );
          const hasMultipleAnswers = sameResponse?.length > 1;

          return (
            <div
              key={question.questionId}
              className={`w-full mb-3 mt-0 p-2 md:p-4 border-2 
                ${hasMultipleAnswers ? "border-green-600" : "border-transparent"}
                rounded-lg flex flex-col space-y-3`}
            >
              {sameResponse?.length ? (
                sameResponse.map((answer) => {
                  const answerIdNumber = parseInt(answer.answerId.split("_")[2]);

                  return (
                    <div key={answer.answerId} className="w-full mb-0 mt-0 p-4 flex flex-row bg-[#E6F4F1] rounded-lg">
                      <div className="flex-1 mb-3 md:mb-0 md:mr-4">
                        <QuestionGenerator
                          text={question.text}
                          inputFields={question.inputFields}
                          handleInputChange={handleInputChange}
                          pillarName={Data.pillarName}
                          answer={answer}
                        />
                      </div>

                      <div className="flex flex-col space-y-2 justify-center items-center">
                        {answerIdNumber === sameResponse?.length && (
                          <AddButton
                            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                            onClick={() => {
                              handleAddQuestion(Data.pillarName, question.questionId);
                            }}
                          />
                        )}
                        <DeleteButton
                          className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                          onClick={() => {
                            handleDeleteQuestion(
                              Data.pillarName,
                              question.questionId,
                              answer.answerId
                            );
                          }}
                        />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="w-full mb-0 mt-0 p-4 flex flex-row bg-[#E6F4F1] rounded-lg">
                  <div className="flex-1 mb-3 md:mb-0 md:mr-4">
                    <QuestionGenerator
                      text={question.text}
                      inputFields={question.inputFields}
                      handleInputChange={handleInputChange}
                      pillarName={Data.pillarName}
                      answer={{
                        questionId: question.questionId,
                        response: [],
                      }}
                    />
                  </div>
                  <div className="flex flex-col space-y-2 justify-center items-center">
                    <AddButton
                      className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                      onClick={() => {
                        handleAddQuestion(Data.pillarName, question.questionId);
                      }}
                    />
                    <DeleteButton
                      className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                      onClick={() => {
                        handleDeleteQuestion(Data.pillarName, question.questionId);
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex justify-center items-start h-full  py-4 ">
      {loading ? <Spin size="large" /> : questionContent(Data)}
    </div>
  );
};

export default GenerateTabContent;